import { useEffect, useState } from "react";
import { api, apiError, fileUrl, money } from "@/lib/api";
import { StatusPill, Modal, Select, Text } from "@/components/admin/AdminUI";
import { toast } from "sonner";
import { Eye, Truck, Loader2 } from "lucide-react";
import { DeleteOrderButton } from "@/components/admin/DeleteOrderButton";

const STATUSES = ["Pending Payment", "Paid", "Processing", "Shipped", "Delivered", "Cancelled", "Refunded"];

export default function AdminOrders() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [edit, setEdit] = useState({ status: "", tracking_carrier: "", tracking_number: "" });
  const [buying, setBuying] = useState(false);

  const load = () => api.get("/admin/orders").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openO = (o) => { setActive(o); setEdit({ status: o.status, tracking_carrier: o.tracking?.carrier || "", tracking_number: o.tracking?.number || "" }); };
  const save = async () => {
    try { await api.patch(`/admin/orders/${active.id}`, edit); toast.success("Updated"); setActive(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const buyLabel = async () => {
    setBuying(true);
    try {
      const { data } = await api.post(`/admin/orders/${active.id}/label`);
      toast.success(data.already_purchased ? "Label already purchased" : "Shipping label purchased");
      const { data: fresh } = await api.get(`/admin/orders/${active.id}`);
      setActive(fresh);
      setEdit((p) => ({ ...p, tracking_carrier: fresh.tracking?.carrier || "", tracking_number: fresh.tracking?.number || "" }));
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBuying(false); }
  };

  return (
    <div>
      <h1 className="font-serif text-3xl text-kys-ink">Ready-to-Ship Orders</h1>
      <p className="text-sm text-kys-muted">Payment, shipping labels & tracking activate once integrations are configured. Labels are never purchased automatically.</p>

      <div className="mt-6 overflow-x-auto rounded-lg border border-kys-blush-border bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-kys-blush-border bg-kys-blush text-left text-xs uppercase tracking-wide text-kys-muted">
            <tr><th className="p-3">Order #</th><th className="p-3">Customer</th><th className="p-3">Items</th><th className="p-3">Subtotal</th><th className="p-3">Status</th><th className="sticky right-0 bg-kys-blush p-3 text-right shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-kys-blush-border">
            {items.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.order_number}`}>
                <td className="p-3 font-mono text-xs">{o.order_number}</td>
                <td className="p-3">{o.customer?.email}</td>
                <td className="p-3">{o.items?.length}</td>
                <td className="p-3 font-mono">{money(o.subtotal)}</td>
                <td className="p-3"><StatusPill status={o.status} /></td>
                <td className="sticky right-0 bg-white p-1.5 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`view-order-${o.order_number}`} aria-label="View order" onClick={() => openO(o)} className="flex h-10 w-10 items-center justify-center rounded-full text-kys-charcoal transition-colors hover:bg-kys-blush hover:text-kys-accent"><Eye className="h-5 w-5" /></button>
                    <DeleteOrderButton order={o} onDeleted={load} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="p-8 text-center text-sm text-kys-muted">No orders yet.</p>}
      </div>

      <Modal open={!!active} onClose={() => setActive(null)} title={active?.order_number} wide>
        {active && (
          <div className="space-y-4">
            <div className="rounded border border-kys-blush-border p-3 text-sm">
              <p className="text-kys-muted">Ship to</p>
              <p className="font-medium text-kys-ink">{active.shipping_address?.full_name}</p>
              <p>{active.shipping_address?.line1} {active.shipping_address?.line2}</p>
              <p>{active.shipping_address?.city}, {active.shipping_address?.state} {active.shipping_address?.postal_code} ({active.shipping_address?.country})</p>
              <p className="mt-1 text-xs text-kys-muted">{active.customer?.email} · {active.customer?.phone}</p>
            </div>
            {active.items?.map((it, i) => (
              <div key={i} className="flex items-center gap-3 rounded border border-kys-blush-border p-2 text-sm">
                <div className="h-12 w-10 overflow-hidden rounded bg-kys-blush">{it.image && <img src={fileUrl(it.image)} alt="" className="h-full w-full object-cover" />}</div>
                <span className="flex-1">{it.name} {it.size ? `· ${it.size}` : ""} × {it.quantity}</span>
                <span className="font-mono">{money(it.price * it.quantity)}</span>
              </div>
            ))}
            <div className="rounded bg-kys-blush p-3 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{money(active.subtotal)}</span></div>
              <div className="flex justify-between"><span>Shipping{active.shipping_method?.provider ? ` (${active.shipping_method.provider} ${active.shipping_method.servicelevel || ""})` : ""}</span><span className="font-mono">{money(active.shipping_amount)}</span></div>
              <div className="flex justify-between"><span>Sales tax</span><span data-testid="order-tax" className="font-mono">{active.tax_amount != null ? money(active.tax_amount) : "—"}</span></div>
              <div className="mt-1 flex justify-between border-t border-kys-blush-border pt-1 font-semibold"><span>Total</span><span className="font-mono">{money(active.total)}</span></div>
            </div>

            <div className="rounded border border-kys-blush-border p-3">
              <p className="mb-2 flex items-center gap-2 font-medium text-kys-ink"><Truck className="h-4 w-4 text-kys-accent" /> Shipping Label</p>
              {active.label_url ? (
                <div className="space-y-1 text-sm">
                  <p className="text-green-700">Label purchased ✓</p>
                  <p className="text-kys-charcoal">{active.tracking?.carrier} · <span className="font-mono">{active.tracking?.number}</span></p>
                  <a data-testid="label-link" href={active.label_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-kys-accent hover:underline">Open / print label →</a>
                </div>
              ) : active.shipping_method?.rate_id ? (
                <>
                  <p className="mb-2 text-xs text-kys-muted">Purchase the Shippo label for this order. Tracking will be saved automatically. Labels are never bought automatically.</p>
                  <button data-testid="buy-label-btn" onClick={buyLabel} disabled={buying}
                    className="flex items-center justify-center gap-2 rounded-full bg-kys-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-kys-accent disabled:opacity-60">
                    {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buy Shipping Label"}
                  </button>
                </>
              ) : (
                <p className="text-xs text-kys-muted">This order has no live shipping selection (placed before shipping was configured). Add tracking manually below.</p>
              )}
            </div>
            <div className="border-t border-kys-blush-border pt-4">
              <Select label="Order status" value={edit.status} onChange={(v) => setEdit({ ...edit, status: v })} options={STATUSES.map((s) => ({ value: s, label: s }))} testid="order-status" />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Text label="Tracking carrier" value={edit.tracking_carrier} onChange={(v) => setEdit({ ...edit, tracking_carrier: v })} />
                <Text label="Tracking number" value={edit.tracking_number} onChange={(v) => setEdit({ ...edit, tracking_number: v })} />
              </div>
              <button data-testid="save-order" onClick={save} className="mt-3 w-full rounded-full bg-kys-ink py-3 text-sm font-semibold text-white hover:bg-kys-accent">Save</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
